/**
 * Ad Account Manager Types
 * E007-S029: Settings page for managing connected ad accounts
 */

// Account status indicators
export type AccountStatus = 'connected' | 'warning' | 'disconnected';

// Platform types
export type Platform = 'facebook' | 'google' | 'tiktok';

// Account type categories
export type AccountType = 
  | 'ecommerce' 
  | 'lead_gen' 
  | 'app_install' 
  | 'brand_awareness' 
  | 'traffic' 
  | 'other';

// Special ad categories (Facebook)
export type SpecialAdCategory = 
  | 'none'
  | 'housing'
  | 'employment'
  | 'credit'
  | 'issues_elections_politics';

// Industry categories
export type Industry = 
  | 'ecommerce'
  | 'saas'
  | 'finance'
  | 'healthcare'
  | 'education'
  | 'real_estate'
  | 'automotive'
  | 'travel'
  | 'food_beverage'
  | 'entertainment'
  | 'other';

// Available metrics for top 3 selection
export const AVAILABLE_METRICS = [
  { value: 'roas', label: 'ROAS' },
  { value: 'cpm', label: 'CPM' },
  { value: 'ctr', label: 'CTR' },
  { value: 'cpc', label: 'CPC' },
  { value: 'cpa', label: 'CPA' },
  { value: 'conversion_rate', label: 'Conv. Rate' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'spend', label: 'Spend' },
  { value: 'conversions', label: 'Conversions' },
];

// Account configuration
export interface AdAccountConfig {
  facebook_page_id?: string;
  instagram_profile_id?: string;
  pixel_id?: string;
  industry?: string;
  top_metrics?: string[];
  name_stamp?: string;
  special_ad_category?: SpecialAdCategory;
  default_exclusions?: string[];
}

// Single ad account
export interface AdAccount {
  id: string;
  platform: Platform;
  platform_account_id: string;
  name: string;
  status: AccountStatus;
  account_type: AccountType;
  config: AdAccountConfig;
  last_30_day_spend: number;
  currency: string;
  timezone?: string;
  token_expires_at?: string;
  last_sync_at?: string;
  connected_at?: string;
}

// Response for listing accounts grouped by platform
export interface AdAccountListResponse {
  facebook: AdAccount[];
  google: AdAccount[];
  tiktok: AdAccount[];
  facebook_total_spend: number;
  google_total_spend: number;
  tiktok_total_spend: number;
}

// OAuth initiation response
export interface OAuthInitiateResponse {
  oauth_url: string;
  state: string;
  platform: Platform;
}

// Available account after OAuth
export interface AvailableAccount {
  id: string;
  name: string;
  currency: string;
  timezone: string;
}

// OAuth callback response
export interface OAuthCallbackResponse {
  available_accounts: AvailableAccount[];
}

// Request to activate accounts
export interface ActivateAccountsRequest {
  platform: Platform;
  account_ids: string[];
}

// Response after activating accounts
export interface ActivateAccountsResponse {
  connected: number;
  accounts: Array<{
    id: string;
    status: 'connected' | 'already_connected';
  }>;
}

// Request to update account
export interface UpdateAccountRequest {
  account_type?: AccountType;
  config?: Partial<AdAccountConfig>;
}

// Audit log entry
export interface AuditLogEntry {
  id: string;
  ad_account_id?: string;
  user_id?: string;
  action: string;
  platform?: Platform;
  details: Record<string, any>;
  created_at?: string;
}

// Audit log response
export interface AuditLogResponse {
  entries: AuditLogEntry[];
}

// Platform display info
export const PLATFORM_INFO: Record<Platform, {
  name: string;
  icon: string;
  color: string;
  bgColor: string;
}> = {
  facebook: {
    name: 'Facebook',
    icon: '📘',
    color: '#1877F2',
    bgColor: 'bg-blue-100',
  },
  google: {
    name: 'Google Ads',
    icon: '🔍',
    color: '#4285F4',
    bgColor: 'bg-red-100',
  },
  tiktok: {
    name: 'TikTok',
    icon: '🎵',
    color: '#000000',
    bgColor: 'bg-gray-100',
  },
};

// Account type labels
export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  ecommerce: 'E-commerce',
  lead_gen: 'Lead Generation',
  app_install: 'App Install',
  brand_awareness: 'Brand Awareness',
  traffic: 'Traffic',
  other: 'Other',
};

// Status display info
export const STATUS_INFO: Record<AccountStatus, {
  label: string;
  color: string;
  bgColor: string;
  dotColor: string;
}> = {
  connected: {
    label: 'Connected',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    dotColor: 'bg-green-500',
  },
  warning: {
    label: 'Needs Attention',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    dotColor: 'bg-yellow-500',
  },
  disconnected: {
    label: 'Disconnected',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    dotColor: 'bg-red-500',
  },
};

// Timezone options
export const TIMEZONE_OPTIONS = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (GMT-8:00)' },
  { value: 'America/Denver', label: 'Mountain Time (GMT-7:00)' },
  { value: 'America/Chicago', label: 'Central Time (GMT-6:00)' },
  { value: 'America/New_York', label: 'Eastern Time (GMT-5:00)' },
  { value: 'Europe/London', label: 'London (GMT+0:00)' },
  { value: 'Europe/Paris', label: 'Paris (GMT+1:00)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (GMT+9:00)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (GMT+8:00)' },
  { value: 'Australia/Sydney', label: 'Sydney (GMT+11:00)' },
];
